import { useEffect, useState } from "react";

/**
 * Holds the value a transient surface (popover, hover card, dialog) opened with, for as long as
 * it stays open, and returns the live value again once it closes.
 *
 * Built for multi-select pickers that float the current selection to the top of the list. Ticking
 * a row changes the selection, the list regroups the instant it does, and the row leaves its group
 * to re-land above the separator — which takes the scroll position with it and puts a different
 * row under the cursor mid-click. Worse, moving a row between two `CommandGroup`s unmounts and
 * remounts it, and cmdk's unmount cleanup resets the highlight to the first item and scrolls the
 * list back to the top. Snapshotting the grouping input per opening makes a ticked row check *in
 * place*; closing drops the snapshot, so reopening shows the regrouped list.
 *
 * **Snapshot the grouping input, never the selection itself.** Checkboxes, counts and trigger
 * labels must keep reading live state, or a click stops filling its own checkbox. Nothing about
 * WHAT is selected belongs here — this is a layout snapshot, not a draft.
 *
 * State rather than a ref: the rendered list derives from the result, and a ref write would
 * neither re-derive it nor clear safely on close.
 *
 * @param value - The live value to capture. Re-allocating it every render is fine; only the first
 *   value seen after opening is kept.
 * @param options.open - Whether the surface is open. Closing always drops the snapshot.
 * @param options.ready - Withhold the capture while the value is still loading, so an opening that
 *   races a pending fetch does not pin an empty list until it is closed and reopened. It only
 *   delays the capture — going false again later never drops one.
 * @returns The value captured on opening, or the live value while closed or not yet captured.
 */
export function useOpenSnapshot<T>(
  value: T,
  { open, ready = true }: { open: boolean; ready?: boolean },
): T {
  // Boxed so that "nothing captured yet" stays distinguishable from a captured `null`/`undefined`.
  const [snapshot, setSnapshot] = useState<{ value: T } | null>(null);

  useEffect(() => {
    if (!open) {
      setSnapshot(null);
      return;
    }
    // Skip, never clear: a background refetch flipping this false mid-opening would otherwise drop
    // the snapshot and re-shuffle the list under the cursor — the very bug this hook exists for.
    if (!ready) return;
    // `current ?? …`, not a bare write: this re-runs on every upstream change, and keeping only
    // the first capture is the whole point.
    setSnapshot((current) => current ?? { value });
  }, [open, ready, value]);

  return snapshot ? snapshot.value : value;
}
